/**
 * Vom Kunden anpassbares PDF-Layout für Angebot & Rechnung.
 * Wird als JSON im OrganizationSetting `pdf_layout` gespeichert.
 *
 * Jedes benannte Element (`logo`, `title`, `metaBox`, `recipient`, `senderLine`) im Kopfbereich
 * ist frei auf einer Zeichenfläche positionierbar (x/y in PDF-Punkten, Ursprung oben links der
 * Fläche) — passend zum visuellen Editor in den Einstellungen (Settings → PDF-Layout), der genau
 * diese Elementnamen für Auswahl/Verschieben/Drag&Drop verwendet. Die Positionstabelle, der
 * Summenblock und die Fußzeile bleiben bewusst NICHT frei positionierbar (Fließtext), damit der
 * automatische Seitenumbruch bei mehrseitigen Dokumenten zuverlässig funktioniert — dort lassen
 * sich nur Schriftgröße/Sichtbarkeit einstellen.
 */
export interface PdfLayout {
  /** Akzentfarbe (Balken, Titel). Leer ⇒ Branding-Akzent. */
  accentColor: string | null
  /** Höhe der Kopf-Zeichenfläche (logo/title/metaBox/recipient/senderLine), in PDF-Punkten. */
  canvasHeight: number
  logo: { x: number; y: number; maxW: number; maxH: number }
  bar: { visible: boolean; y: number; thickness: number }
  /** fontSize = Überschrift (RECHNUNG/ANGEBOT); subtitleFontSize = Zeile darunter (Betreff, bei Angeboten auch die Nummer +1 pt) */
  title: { x: number; y: number; fontSize: number; subtitleFontSize: number }
  metaBox: { x: number; y: number; width: number; fontSize: number }
  recipient: { x: number; y: number; fontSize: number }
  senderLine: { x: number; y: number; visible: boolean; fontSize: number }
  /** Einleitungstext über der Tabelle (Fließtext, nicht verschiebbar). */
  intro: { fontSize: number }
  /** Positionstabelle (Fließtext — Seitenumbruch/Kopfwiederholung übernimmt der Browser). */
  table: { fontSize: number; headerFontSize: number }
  /** Summenblock unter der Tabelle. */
  summary: { fontSize: number }
  /** Firmen-Fußzeile (Bank/UID/…), wiederholt sich automatisch auf jeder Seite. */
  footer: { visible: boolean; fontSize: number; /** Höhe des Farbbalkens in pt; 0 = automatisch nach Inhalt/Schriftgröße */ height: number }
  /** Logo + Farbbalken auf jeder Folgeseite wiederholen (Seite 2, 3, …) */
  repeatHeader: boolean
  /** SEPA-QR-Code zum Bezahlen unter der Summe — nur auf offenen Rechnungen. size = Kantenlänge in pt. */
  qrCode: { visible: boolean; size: number }
}

export const DEFAULT_PDF_LAYOUT: PdfLayout = {
  accentColor: null,
  canvasHeight: 280,
  logo: { x: 0, y: 0, maxW: 180, maxH: 44 },
  bar: { visible: true, y: 64, thickness: 8 },
  senderLine: { x: 0, y: 88, visible: true, fontSize: 7.5 },
  recipient: { x: 0, y: 106, fontSize: 10 },
  metaBox: { x: 265, y: 88, width: 230, fontSize: 8.5 },
  title: { x: 0, y: 210, fontSize: 18, subtitleFontSize: 11 },
  intro: { fontSize: 9 },
  table: { fontSize: 8.5, headerFontSize: 8 },
  summary: { fontSize: 9 },
  footer: { visible: true, fontSize: 6.5, height: 0 },
  repeatHeader: true,
  qrCode: { visible: true, size: 85 },
}

/** Namen + Reihenfolge aller editierbaren Elemente — für UI-Beschriftung & Konsistenz. */
export const PDF_ELEMENT_LABELS: Record<string, string> = {
  logo: 'Logo',
  bar: 'Farbbalken',
  senderLine: 'Absenderzeile',
  recipient: 'Empfänger-Adresse',
  metaBox: 'Info-Kasten',
  title: 'Titelblock',
  intro: 'Einleitungstext',
  table: 'Positionstabelle',
  summary: 'Summenblock',
  footer: 'Fußzeile',
  qrCode: 'QR-Code (Zahlung)',
}

/** Diese Elemente lassen sich im Editor frei per Drag&Drop verschieben (x/y). Der Rest ist
 * Fließtext (Tabelle/Summe/Fußzeile) und bleibt an seiner natürlichen Position. */
export const PDF_DRAGGABLE_ELEMENTS = ['logo', 'senderLine', 'recipient', 'metaBox', 'title'] as const

/** Tolerantes Zusammenführen: fehlende/ungültige Felder fallen auf Default zurück. */
export function mergePdfLayout(raw: unknown): PdfLayout {
  const d = DEFAULT_PDF_LAYOUT
  const num = (v: unknown, fb: number) => (typeof v === 'number' && Number.isFinite(v) ? v : fb)
  const bool = (v: unknown, fb: boolean) => (typeof v === 'boolean' ? v : fb)
  const obj = (v: unknown) => (v && typeof v === 'object' ? (v as Record<string, unknown>) : {})
  if (!raw || typeof raw !== 'object') return JSON.parse(JSON.stringify(d))
  const o = raw as Record<string, unknown>
  const lg = obj(o.logo)
  const br = obj(o.bar)
  const mb = obj(o.metaBox)
  const ti = obj(o.title)
  const rc = obj(o.recipient)
  const sl = obj(o.senderLine)
  const it = obj(o.intro)
  const tb = obj(o.table)
  const sm = obj(o.summary)
  const ft = obj(o.footer)
  const qr = obj(o.qrCode)
  return {
    accentColor: typeof o.accentColor === 'string' && o.accentColor.trim() ? o.accentColor.trim() : null,
    canvasHeight: num(o.canvasHeight, d.canvasHeight),
    logo: { x: num(lg.x, d.logo.x), y: num(lg.y, d.logo.y), maxW: num(lg.maxW, d.logo.maxW), maxH: num(lg.maxH, d.logo.maxH) },
    bar: { visible: bool(br.visible, d.bar.visible), y: num(br.y, d.bar.y), thickness: num(br.thickness, d.bar.thickness) },
    title: { x: num(ti.x, d.title.x), y: num(ti.y, d.title.y), fontSize: num(ti.fontSize, d.title.fontSize), subtitleFontSize: num(ti.subtitleFontSize, d.title.subtitleFontSize) },
    metaBox: { x: num(mb.x, d.metaBox.x), y: num(mb.y, d.metaBox.y), width: num(mb.width, d.metaBox.width), fontSize: num(mb.fontSize, d.metaBox.fontSize) },
    recipient: { x: num(rc.x, d.recipient.x), y: num(rc.y, d.recipient.y), fontSize: num(rc.fontSize, d.recipient.fontSize) },
    senderLine: { x: num(sl.x, d.senderLine.x), y: num(sl.y, d.senderLine.y), visible: bool(sl.visible, d.senderLine.visible), fontSize: num(sl.fontSize, d.senderLine.fontSize) },
    intro: { fontSize: num(it.fontSize, d.intro.fontSize) },
    table: { fontSize: num(tb.fontSize, d.table.fontSize), headerFontSize: num(tb.headerFontSize, d.table.headerFontSize) },
    summary: { fontSize: num(sm.fontSize, d.summary.fontSize) },
    footer: {
      visible: bool(ft.visible, d.footer.visible),
      fontSize: num(ft.fontSize, d.footer.fontSize),
      height: Math.min(200, Math.max(0, num(ft.height, d.footer.height))),
    },
    repeatHeader: bool(o.repeatHeader, d.repeatHeader),
    qrCode: { visible: bool(qr.visible, d.qrCode.visible), size: Math.min(150, Math.max(50, num(qr.size, d.qrCode.size))) },
  }
}
